import { useCallback, useContext, useEffect, useRef, useState } from "react";

import { getNotifications } from "../api/notificationsService";
import {
   getCurrentPopup,
   subscribePopup,
} from "../components/Common/popupUIStore";
import { notificationId } from "../utils/notificationsFormat";
import { UserContext } from "../UserContext";

const POLL_MS = 15_000;
const MAX_QUEUE = 5;

export default function useNotificationToasts() {
   const { user, loading } = useContext(UserContext);
   const canAccess = Boolean(user?.id);

   const [queue, setQueue] = useState([]);
   const seenRef = useRef(new Set());
   const initializedRef = useRef(false);
   const popupOpenRef = useRef(false);

   const enqueue = useCallback((items) => {
      if (popupOpenRef.current) return;
      setQueue((prev) => {
         const next = [...prev];
         for (const item of items) {
            const id = String(notificationId(item) ?? "");
            if (!id || seenRef.current.has(id)) continue;
            seenRef.current.add(id);
            if (!next.some((x) => String(notificationId(x)) === id)) {
               next.push(item);
            }
         }
         return next.slice(-MAX_QUEUE);
      });
   }, []);

   const poll = useCallback(async () => {
      if (!canAccess || popupOpenRef.current) return;
      try {
         const { items } = await getNotifications({
            page: 1,
            limit: 10,
            unreadOnly: true,
         });

         if (!initializedRef.current) {
            initializedRef.current = true;
            items.forEach((item) => {
               const id = String(notificationId(item) ?? "");
               if (id) seenRef.current.add(id);
            });
            return;
         }

         const fresh = items.filter((item) => {
            const id = String(notificationId(item) ?? "");
            return id && !seenRef.current.has(id);
         });

         if (fresh.length) {
            enqueue([...fresh].reverse());
         }
      } catch {
         // silent
      }
   }, [canAccess, enqueue]);

   const dismissCurrent = useCallback(() => {
      setQueue((prev) => prev.slice(1));
   }, []);

   useEffect(() => {
      if (loading || !canAccess) return undefined;
      poll();
      const timer = setInterval(poll, POLL_MS);
      return () => clearInterval(timer);
   }, [canAccess, loading, poll]);

   useEffect(() => {
      if (!canAccess) return undefined;

      const handleFocus = () => poll();
      const handleVisibility = () => {
         if (document.visibilityState === "visible") poll();
      };

      window.addEventListener("focus", handleFocus);
      document.addEventListener("visibilitychange", handleVisibility);
      return () => {
         window.removeEventListener("focus", handleFocus);
         document.removeEventListener("visibilitychange", handleVisibility);
      };
   }, [canAccess, poll]);

   useEffect(() => {
      if (!canAccess) return undefined;

      popupOpenRef.current = getCurrentPopup()?.type === "notifications";
      return subscribePopup(() => {
         popupOpenRef.current = getCurrentPopup()?.type === "notifications";
      });
   }, [canAccess]);

   const current = queue[0] ?? null;

   return { canAccess, current, dismissCurrent, queueLength: queue.length };
}
